import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import { X, Download } from 'lucide-react';
import { toast } from 'react-toastify';

const ImagePreviewModal = ({ open, onClose, imageUrl, imageName }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      const sanitizedName = imageName
        ?.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        || 'image';
      
      // Create a blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${sanitizedName}.jpg`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Failed to download image');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          bgcolor: 'rgba(0, 0, 0, 0.95)',
          backgroundImage: 'none',
          maxHeight: '90vh',
          m: { xs: 1, sm: 2 },
        }
      }}
    >
      <DialogContent sx={{ 
        p: 0, 
        position: 'relative',
        height: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {/* Close button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.7)',
            },
            zIndex: 1,
          }}
        >
          <X size={20} />
        </IconButton>

        {/* Download button */}
        <Button
          variant="contained"
          startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <Download size={18} />}
          onClick={handleDownload}
          disabled={isDownloading}
          sx={{
            position: 'absolute',
            bottom: 16,
            right: 16,
            bgcolor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.85)',
            },
            zIndex: 1,
            minWidth: 120,
          }}
        >
          {isDownloading ? 'Downloading...' : 'Download'}
        </Button>

        {/* Image */}
        <img
          src={imageUrl}
          alt={imageName || "Preview"}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default ImagePreviewModal; 